import type { PlanHuntStand } from './types'

/**
 * Assign hunters to stands as a 1:1 matching — each hunter lands on at most one
 * stand, each stand gets at most one hunter. Three outcomes fall out naturally:
 *   - equal counts → every stand filled, every hunter placed;
 *   - more hunters than stands → surplus hunters go unassigned;
 *   - more stands than hunters → surplus stands (at the bottom) stay empty.
 *
 * `orderedPool` is the included hunter ids in the desired assignment order
 * (the caller shuffles), so this stays pure/deterministic and unit-testable.
 *
 * With `keepExisting`, a stand whose current assignee is still in the pool is
 * left as-is and that hunter is reserved (never handed to another stand);
 * remaining stands fill from the rest of the pool in order.
 */
export function assignStandsInOrder(
  stands: PlanHuntStand[],
  orderedPool: string[],
  keepExisting: boolean,
): PlanHuntStand[] {
  const inPool = new Set(orderedPool)

  const reserved = new Set<string>()
  if (keepExisting) {
    for (const s of stands) {
      if (s.assignedId && inPool.has(s.assignedId)) reserved.add(s.assignedId)
    }
  }

  // Available hunters, in shuffled order, minus any reserved by a kept stand.
  const queue = orderedPool.filter((id) => !reserved.has(id))
  let next = 0

  return stands.map((s) => {
    if (keepExisting && s.assignedId && inPool.has(s.assignedId)) return s
    const assignedId = next < queue.length ? queue[next++] : null
    return { ...s, assignedId }
  })
}
